/**
 * @file Precomputed values and Padé approximant coefficients for inverse
 * hyperbolic functions.
 *
 * @internal
 */

import type { TwoF64 } from '../base/common.js';

/**
 * Coefficients (from lowest to highest degree) of the Padé (n,n) approximant
 * for asinh(x). Trivial zeros are skipped, which means :
 * - for the numerator:   index `k` maps to degree `2k + 1`
 * - for the denominator: index `k` maps to degree `2k`
 *
 * @internal
 */
export const asinh_pade = {
  23: [
    [
      [1.0, 0.0],
      [5.273619059511041, -2.558387161446567e-16],
      [11.990783266572592, 2.699032451119377e-16],
      [15.382408689142517, -8.04256379725191e-16],
      [12.233619874926953, 5.923519932290373e-16],
      [6.240622897917647, 3.4128071076655785e-16],
      [2.041956844722836, -8.709712056417669e-17],
      [0.4162984577112436, -1.2595866075831891e-17],
      [0.04956161967987337, 3.3094215705644163e-18],
      [0.003041071420887146, 1.476138324496372e-19],
      [7.407072394709387e-5, -4.840869856892069e-21],
      [3.305750850136934e-7, -1.2575927031867814e-23],
    ],
    [
      [1.0, 0.0],
      [5.440285726177707, 3.362802303220934e-16],
      [12.822497554268876, 3.5555589748895743e-16],
      [17.156113042533526, -1.5219976717116516e-15],
      [14.343772686158006, 8.32623573477751e-16],
      [7.774061827680102, 8.998178434094974e-17],
      [2.7425345332356854, 1.8013984326064484e-16],
      [0.6158716334610623, 4.438643048089941e-17],
      [0.08351587886873667, -8.142198390300769e-19],
      [0.006189338164638956, 4.326480603212496e-19],
      [0.00020622175986927037, 1.121687581927064e-20],
      [1.9239597389260916e-6, 1.3568562579089967e-22],
    ],
  ] as [TwoF64[], TwoF64[]],
  24: [
    [
      [1.0, 0.0],
      [5.5217381937720615, 9.474022968879775e-17],
      [13.23674645584473, 2.1122220852091901e-16],
      [18.05908611836094, 5.37289880786265e-18],
      [15.445888323955353, -5.758861796625732e-16],
      [8.599702208136929, 3.387337370669149e-17],
      [3.1339033121265167, -1.5047751523684625e-16],
      [0.7325847227775485, 2.9760766588331795e-18],
      [0.10457728901750483, 3.654293373956352e-18],
      [0.008304017494057844, 8.193263197373317e-19],
      [0.00030598067233726835, 1.9526847551800357e-20],
      [3.3944082724018485e-6, -1.6592701119309695e-22],
    ],
    [
      [1.0, 0.0],
      [5.6884048604387285, -2.0131924354457732e-16],
      [14.109813932584517, 3.553046852035145e-16],
      [20.028734266401646, 1.0080472092465654e-15],
      [17.949339357855976, 1.4741763636190577e-15],
      [10.568553464147685, 8.164891089682005e-16],
      [4.12449391131941, -1.9513331228202466e-16],
      [1.0510790621467399, -1.9004468443822514e-17],
      [0.1680223170199251, -5.463494311104553e-18],
      [0.015615999948395971, 5.607070488972129e-19],
      [0.0007327377217025146, 4.9109464048753004e-20],
      [1.286549986186927e-5, 6.2438159815435075e-22],
      [3.0750221161934464e-8, 2.825979090385633e-24],
    ],
  ] as [TwoF64[], TwoF64[]],
  25: [
    [
      [1.0, 0.0],
      [5.773536659956517, 2.499001563577705e-16],
      [14.564635646270721, -7.116774171660272e-16],
      [21.078346953234156, -7.380601859726813e-17],
      [19.319155238103882, -1.1413678417611188e-15],
      [11.680108928527208, -3.6859752371370374e-16],
      [4.7052825858577725, 1.7493679968510534e-16],
      [1.2467463242936092, -5.0750797590537705e-17],
      [0.20940060214415043, -1.0815621971858014e-17],
      [0.020783557311067288, 1.0614802262890162e-18],
      [0.0010717177916740347, 8.910822337987105e-20],
      [2.204530295145231e-5, 4.776085312255094e-23],
      [8.323829936524401e-8, -4.419445725217229e-24],
    ],
    [
      [1.0, 0.0],
      [5.940203326623184, -4.615931687560462e-17],
      [15.479669534041252, -2.456754794718945e-16],
      [23.257419483220485, -1.4477870336732925e-15],
      [22.269222307653052, 6.814439863288127e-16],
      [14.18029342582076, 3.023267208130291e-16],
      [6.081990755552385, 1.1316731919170236e-16],
      [1.7416450510733874, -6.077434959822141e-17],
      [0.32309784969193583, 4.6165190919204216e-18],
      [0.03664517014959065, 1.7847548807424954e-18],
      [0.0022912035782555176, -1.3123623148596213e-19],
      [6.479182269081067e-5, 4.5014961111357546e-21],
      [5.152003324241701e-7, -5.1380499855243576e-23],
    ],
  ] as [TwoF64[], TwoF64[]],
  26: [
    [
      [1.0, 0.0],
      [6.021825891614485, 8.306267409224574e-17],
      [15.935623779818842, -3.9924821386094816e-16],
      [24.364888560407174, 9.115523649818412e-16],
      [23.803031854876792, 1.4399267686959294e-15],
      [15.515156672756861, -7.217516791341468e-16],
      [6.840496039603421, 2.4292420920129953e-16],
      [2.024860769751863, -1.253622615342802e-16],
      [0.3912935000869534, -1.0284012134023445e-17],
      [0.046748972050096815, 3.9291119357232314e-19],
      [0.0031335159149479814, 6.119677601676857e-20],
      [9.801700898826112e-5, -2.7422122495670493e-21],
      [9.260333212533493e-7, 1.780682280183938e-23],
    ],
    [
      [1.0, 0.0],
      [6.188492558281152, -2.1299679914112934e-16],
      [16.892039206199033, 1.869772134056179e-16],
      [26.760734343378783, 1.716813716348112e-15],
      [27.242141349739533, -1.0231553269873048e-15],
      [18.636921103750947, -9.531421091564578e-16],
      [8.70604877490177, -2.966281645962223e-16],
      [2.7657124752928506, 1.380487398870196e-16],
      [0.5840694624441274, 3.876289190889445e-17],
      [0.07836330214385936, -3.086040050322331e-19],
      [0.00616848820701362, 1.7782764591143423e-19],
      [0.000246722708619138, 1.1069633872067745e-20],
      [3.709565408023384e-6, -3.063154477679588e-24],
      [7.634948785609296e-9, -8.124919384181399e-25],
    ],
  ] as [TwoF64[], TwoF64[]],
  27: [
    [
      [1.0, 0.0],
      [6.273467445665173, -8.922623917066904e-17],
      [17.388485517298918, 8.465553199212899e-16],
      [28.029956108616787, -1.0098356194699985e-15],
      [29.107219839008224, -1.1071902852502964e-15],
      [20.37665891981981, 6.783981543384106e-16],
      [9.779696801859325, -6.467902274145351e-16],
      [3.2089053919047528, 5.39276428588791e-17],
      [0.7050266826391803, -4.121704504082678e-17],
      [0.09944286499280067, 8.998146312222082e-19],
      [0.008363522575406493, 1.3169223509594426e-19],
      [0.0003677057395888868, 1.3939732902608136e-21],
      [6.473547615475028e-6, -7.776224129697325e-23],
      [2.0946215008806403e-8, 1.7043518378971078e-25],
    ],
    [
      [1.0, 0.0],
      [6.44013411233184, -3.852857124040441e-16],
      [18.38684120268756, -1.1124395941729845e-15],
      [30.656062441116013, 1.4643541006623212e-15],
      [33.094674531705884, 8.469364260246236e-16],
      [24.24078281274901, -1.1671781517574094e-15],
      [12.274399849470061, 2.013095594492405e-16],
      [4.296194080741971, 1.858456640574996e-16],
      [1.0223397297009247, -1.0918765831019797e-16],
      [0.1596681471507737, 1.034045696579487e-17],
      [0.015389935771908498, -7.556314630662735e-19],
      [0.0008232847376978536, 2.6750302809247004e-20],
      [2.0013880359715614e-5, -2.2396832062005644e-22],
      [1.3728076108425853e-7, -3.587106844558443e-24],
    ],
  ] as [TwoF64[], TwoF64[]],
  28: [
    [
      [1.0, 0.0],
      [6.521899946350561, 2.26039599875206e-16],
      [18.88450278224472, 6.790296592172175e-16],
      [31.988882974218715, -8.409942130711366e-16],
      [35.16025003299098, -2.665566231632599e-15],
      [26.2901056474208, 1.7710978088846401e-15],
      [13.634165972597893, 5.481086057620196e-16],
      [4.908306928951923, -6.641357774978804e-17],
      [1.208067907208375, -5.595118397754601e-17],
      [0.19665288772134054, 4.51280233904373e-18],
      [0.01997748660628335, 1.0728353851712141e-18],
      [0.001146172934276875, 4.455640782830379e-20],
      [3.082415826813088e-5, -1.1701521624618316e-21],
      [2.510029887257711e-7, 1.6581838929813394e-23],
    ],
    [
      [1.0, 0.0],
      [6.688566613017228, -7.001987335816909e-17],
      [19.92426388441426, -3.3924252866928593e-16],
      [34.85259398278765, 2.7598219293718364e-15],
      [39.74291068480882, -1.4054175868786114e-15],
      [31.00861611228961, 1.080726824181892e-15],
      [16.90441676435119, -1.4036473871827209e-15],
      [6.459059548979776, -3.9424845416368135e-16],
      [1.7094325961147017, -1.0053571077339934e-16],
      [0.30476998427973856, -1.3598011341322976e-17],
      [0.034857926885472096, 1.3214024865405004e-18],
      [0.002355737688414572, 1.1350786158675781e-19],
      [8.130781983784037e-5, 1.3408202138475279e-21],
      [1.0588573718555191e-6, -6.180194031291226e-23],
      [1.8967694194137826e-9, 1.0080787257627721e-25],
    ],
  ] as [TwoF64[], TwoF64[]],
};
